import React, { useState, useEffect } from 'react';
import { QRGenerator } from './QRGenerator';

interface Coupon {
  id: number;
  title: string;
  description: string;
  discount: string;
  vendor_name: string;
  vendor_address: string;
  vendor_category: string;
  driver_name: string;
  max_uses: number;
  current_uses: number;
}

export const TestPage: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [selectedCoupon, setSelectedCoupon] = useState<number | null>(null);
  const [customerPhone, setCustomerPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const response = await fetch('https://cf-api.b-9f2.workers.dev/api/coupons');
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch coupons');
      }

      setCoupons(data.coupons);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleCouponSelect = (couponId: number) => {
    setSelectedCoupon(couponId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando cupons...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h3 className="text-red-800 font-medium mb-2">Erro ao carregar cupons</h3>
          <p className="text-red-600 text-sm mb-4">{error}</p>
          <button
            onClick={fetchCoupons}
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
          >
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-blue-600 text-white rounded-lg p-6 mb-6">
          <h1 className="text-3xl font-bold mb-2">🧪 Test QR Generation</h1>
          <p className="text-blue-100">
            Gere códigos QR para testar o sistema de cupons - São Paulo
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Coupon Selection */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold mb-4">📋 Selecionar Cupom</h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telefone do Cliente (Opcional)
                </label>
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="+5511999887766"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {coupons.map((coupon) => (
                  <div
                    key={coupon.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                      selectedCoupon === coupon.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handleCouponSelect(coupon.id)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-gray-900">{coupon.title}</h3>
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium">
                        {coupon.discount}
                      </span>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">{coupon.vendor_name}</p>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>📍 {coupon.vendor_category}</span>
                      <span>🚗 {coupon.driver_name}</span>
                    </div>
                    
                    <div className="mt-2 text-xs text-gray-500">
                      Uso: {coupon.current_uses}/{coupon.max_uses}
                    </div>
                    
                    {coupon.description && (
                      <p className="text-xs text-gray-600 mt-2 italic">
                        {coupon.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {coupons.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">📭</div>
                  <p>Nenhum cupom disponível</p>
                </div>
              )}
            </div>

            {/* API Testing */}
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h2 className="text-xl font-semibold mb-4">🔗 API Endpoints</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <code>GET /api/coupons</code>
                  <span className="text-green-600">✓ Online</span>
                </div>
                <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <code>POST /api/qr/generate</code>
                  <span className="text-green-600">✓ Online</span>
                </div>
                <div className="flex justify-between items-center p-2 bg-gray-50 rounded">
                  <code>POST /api/qr/validate</code>
                  <span className="text-green-600">✓ Online</span>
                </div>
              </div>
            </div>
          </div>

          {/* QR Generator */}
          <div className="space-y-6">
            {selectedCoupon ? (
              <QRGenerator
                couponId={selectedCoupon}
                customerPhone={customerPhone || undefined}
              />
            ) : (
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="text-center py-8">
                  <div className="text-6xl mb-4">📱</div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Selecione um Cupom
                  </h3>
                  <p className="text-gray-600">
                    Escolha um cupom da lista ao lado para gerar o código QR
                  </p>
                </div>
              </div>
            )}

            {/* Instructions */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-medium text-yellow-800 mb-2">⚠️ Instruções de Teste</h3>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Códigos QR expiram em 5 minutos</li>
                <li>• Cada QR só pode ser usado uma vez</li>
                <li>• Use o Scanner para validar os códigos</li>
                <li>• Tokens JWT são válidos apenas por 5 minutos</li>
              </ul>
            </div>

            {/* Quick Test Button */}
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <button
                onClick={() => window.open('/scanner', '_blank')}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors"
              >
                🔍 Abrir Scanner em Nova Aba
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};